import React, { useState, useEffect } from 'react';
import {
  Settings,
  FileText,
  Monitor,
  Printer,
  RotateCw,
  Maximize2,
  Grid,
  Palette,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import {
  PageTemplate,
  PageSettings,
  PAGE_TEMPLATES,
  getTemplateById,
  getTemplatesByCategory,
  getDefaultTemplate,
  createCustomTemplate,
  flipOrientation,
  validateDimensions,
} from '../types/pageTemplates';

interface PageSettingsProps {
  currentSettings: PageSettings;
  onSettingsChange: (settings: PageSettings) => void;
  onTemplateChange: (template: PageTemplate) => void;
  className?: string;
}

export const PageSettingsPanel: React.FC<PageSettingsProps> = ({
  currentSettings,
  onSettingsChange,
  onTemplateChange,
  className = '',
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<PageTemplate>(
    getTemplateById(currentSettings?.templateId) || getDefaultTemplate()
  );
  const [customWidth, setCustomWidth] = useState<string>('800');
  const [customHeight, setCustomHeight] = useState<string>('600');
  const [showCustomInputs, setShowCustomInputs] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  // Update local state when currentSettings change
  useEffect(() => {
    if (!currentSettings) return;

    const template = getTemplateById(currentSettings.templateId);
    if (template) {
      setSelectedTemplate(template);
      setShowCustomInputs(template.id === 'custom');
      if (currentSettings.customDimensions) {
        setCustomWidth(currentSettings.customDimensions.width.toString());
        setCustomHeight(currentSettings.customDimensions.height.toString());
      }
    }
  }, [currentSettings]);

  const handleTemplateSelect = (templateId: string) => {
    const template = getTemplateById(templateId);
    if (!template) return;

    setSelectedTemplate(template);
    setShowCustomInputs(template.id === 'custom');
    setValidationError('');

    // Update settings
    const newSettings: PageSettings = {
      ...currentSettings,
      templateId: template.id,
      orientation: template.orientation,
      customDimensions: template.id === 'custom' ? currentSettings?.customDimensions : undefined,
    };

    onSettingsChange(newSettings);
    onTemplateChange(template);
  };

  const handleCustomDimensionsChange = () => {
    if (!currentSettings) return;

    const width = parseInt(customWidth);
    const height = parseInt(customHeight);
    
    const validation = validateDimensions(width, height);
    if (!validation.valid) {
      setValidationError(validation.error || 'Invalid dimensions');
      return;
    }

    setValidationError('');
    
    const customTemplate = createCustomTemplate(width, height);
    setSelectedTemplate(customTemplate);

    const newSettings: PageSettings = {
      ...currentSettings,
      templateId: 'custom',
      orientation: customTemplate.orientation,
      customDimensions: { width, height },
    };

    onSettingsChange(newSettings);
    onTemplateChange(customTemplate);
  };

  const handleOrientationFlip = () => {
    if (!currentSettings) return;

    const flippedTemplate = flipOrientation(selectedTemplate);
    setSelectedTemplate(flippedTemplate);

    if (flippedTemplate.id === 'custom') {
      setCustomWidth(flippedTemplate.dimensions.width.toString());
      setCustomHeight(flippedTemplate.dimensions.height.toString());
    }

    const newSettings: PageSettings = {
      ...currentSettings,
      orientation: flippedTemplate.orientation,
      customDimensions: flippedTemplate.id === 'custom' 
        ? flippedTemplate.dimensions 
        : undefined,
    };

    onSettingsChange(newSettings);
    onTemplateChange(flippedTemplate);
  };

  const handleBackgroundColorChange = (color: string) => {
    if (!currentSettings) return;
    onSettingsChange({
      ...currentSettings,
      backgroundColor: color,
    });
  };

  const handleGridToggle = () => {
    if (!currentSettings) return;
    onSettingsChange({
      ...currentSettings,
      showGrid: !currentSettings.showGrid,
    });
  };

  const handleSnapToggle = () => {
    if (!currentSettings) return;
    onSettingsChange({
      ...currentSettings,
      snapToGrid: !currentSettings.snapToGrid,
    });
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'document': return <FileText className="w-4 h-4" />;
      case 'presentation': return <Monitor className="w-4 h-4" />;
      case 'print': return <Printer className="w-4 h-4" />;
      case 'dynamic': return <Maximize2 className="w-4 h-4" />;
      case 'custom': return <Settings className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg ${className}`}>
      {/* Collapsible Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors rounded-t-lg"
      >
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-gray-500" />
          <h3 className="font-medium text-gray-900 text-sm">Page Settings</h3>
        </div>
        {collapsed ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {/* Collapsible Content */}
      {!collapsed && (
        <div className="p-3 pt-0 space-y-3 border-t border-gray-100">

      {/* Template Selection - Compact Dropdown */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Page Template
        </label>

        <select
          value={selectedTemplate.id}
          onChange={(e) => handleTemplateSelect(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
        >
          {['document', 'presentation', 'print', 'dynamic', 'custom'].map((category) => {
            const templates = getTemplatesByCategory(category as PageTemplate['category']);
            if (templates.length === 0) return null;

            return (
              <optgroup key={category} label={category.charAt(0).toUpperCase() + category.slice(1)}>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.dimensions.width}×{template.dimensions.height === 'auto' ? 'auto' : template.dimensions.height})
                  </option>
                ))}
              </optgroup>
            );
          })}
        </select>

        {/* Current Template Info - hidden (was showing wrong orientation for landscape pages)
        <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
          <div className="font-medium">{selectedTemplate.name}</div>
          <div>{selectedTemplate.dimensions.width} × {selectedTemplate.dimensions.height === 'auto' ? 'auto (flexible)' : `${selectedTemplate.dimensions.height} px`}</div>
          <div className="capitalize">{selectedTemplate.orientation}</div>
          {selectedTemplate.isDynamic && (
            <div className="text-blue-600 font-medium mt-1">🔄 Dynamic Height - Auto Expands</div>
          )}
        </div>
        */}
      </div>

      {/* Custom Dimensions - Compact */}
      {showCustomInputs && (
        <div className="space-y-2 p-2 bg-gray-50 rounded">
          <label className="block text-xs font-medium text-gray-700">
            Custom Size (px)
          </label>

          <div className="flex gap-2">
            <input
              type="number"
              value={customWidth}
              onChange={(e) => setCustomWidth(e.target.value)}
              onBlur={handleCustomDimensionsChange}
              className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="W"
              min="100"
              max="5000"
            />
            <span className="text-xs text-gray-400 self-center">×</span>
            <input
              type="number"
              value={customHeight}
              onChange={(e) => setCustomHeight(e.target.value)}
              onBlur={handleCustomDimensionsChange}
              className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="H"
              min="100"
              max="5000"
            />
          </div>

          {validationError && (
            <div className="text-xs text-red-600 bg-red-50 p-1 rounded">
              {validationError}
            </div>
          )}
        </div>
      )}

      {/* Orientation Controls — hidden
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Orientation
        </label>

        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="text-sm text-gray-600">
              Current: <span className="font-medium capitalize">{selectedTemplate.orientation}</span>
            </div>
            <div className="text-xs text-gray-500">
              {selectedTemplate.dimensions.width} × {selectedTemplate.dimensions.height === 'auto' ? 'auto' : selectedTemplate.dimensions.height} px
            </div>
          </div>

          <button
            onClick={handleOrientationFlip}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <RotateCw className="w-4 h-4" />
            Flip
          </button>
        </div>
      </div>
      */}

      {/* Background Color — hidden
      <div className="space-y-2">
        <label className="block text-xs font-medium text-gray-700">
          Background
        </label>

        <div className="flex items-center gap-2">
          <Palette className="w-3 h-3 text-gray-500" />
          <input
            type="color"
            value={currentSettings?.backgroundColor || '#ffffff'}
            onChange={(e) => handleBackgroundColorChange(e.target.value)}
            className="w-8 h-6 border border-gray-300 rounded cursor-pointer"
          />
          <input
            type="text"
            value={currentSettings?.backgroundColor || '#ffffff'}
            onChange={(e) => handleBackgroundColorChange(e.target.value)}
            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="#ffffff"
          />
        </div>
      </div>
      */}

      {/* Grid & Snap — hidden
      <div className="space-y-2">
        <label className="block text-xs font-medium text-gray-700">
          Grid & Snap
        </label>

        <div className="space-y-1">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={currentSettings?.showGrid || false}
              onChange={handleGridToggle}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3 h-3"
            />
            <Grid className="w-3 h-3 text-gray-500" />
            <span className="text-xs text-gray-700">Show Grid</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={currentSettings?.snapToGrid || false}
              onChange={handleSnapToggle}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3 h-3"
            />
            <span className="text-xs text-gray-700">Snap to Grid</span>
          </label>
        </div>
      </div>
      */}
        </div>
      )}
    </div>
  );
};

export default PageSettingsPanel;
